import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { Helpers } from '@app/shared/helpers';
import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import { PageService, PageInterface } from '@app/services/page.service';
import {
	CrossFormationReportService,
	CrossFormationReportPayload,
	PreviewResult,
	PreviewFormation,
} from '@app/services/cross-formation-report.service';
import { ErrorService } from '@app/services/error.service';

interface SurveyTotal {
	survey_id: string;
	label: string;
	results_count: number;
	expected_count: number;
	percent: number;
}

interface Totals {
	formations_count: number;
	rapports_count: number;
	respondants_count: number;
	formateurs_count: number;
	surveys: SurveyTotal[];
}

@Component({
	selector: 'app-molecule-cross-formation-report-filter',
	templateUrl: './molecule-cross-formation-report-filter.component.html',
	styleUrls: ['./molecule-cross-formation-report-filter.component.less'],
})
export class MoleculeCrossFormationReportFilterComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	protected skeleton: PageSkeletonType = null;

	// --- Filter selects ---
	reportOptions: any[] = [];
	reportSearchChange$ = new Subject<string>();
	selectedReport: string = null;
	reportLoading = false;

	themeOptions: any[] = [];
	themeSearchChange$ = new Subject<string>();
	selectedThemes: string[] = [];
	themeLoading = false;

	sourceReportOptions: any[] = [];
	sourceReportSearchChange$ = new Subject<string>();
	selectedSourceReports: string[] = [];
	sourceReportLoading = false;

	formateurOptions: any[] = [];
	formateurSearchChange$ = new Subject<string>();
	selectedFormateurs: string[] = [];
	formateurLoading = false;

	commanditaireOptions: any[] = [];
	commanditaireSearchChange$ = new Subject<string>();
	selectedCommanditaires: string[] = [];
	commanditaireLoading = false;

	withComments = false;

	dateRange: Date[] = [];

	// --- Preview state ---
	private filtersChange$ = new Subject<void>();
	previewResult: PreviewResult = null;
	previewLoading = false;
	excludedFormationIds: { [id: string]: boolean } = {};
	expandedFormationIds: { [id: string]: boolean } = {};
	totals: Totals = null;

	// --- Action state ---
	generating = false;

	constructor(
		protected pageService: PageService,
		protected translate: TranslateService,
		private http: HttpClient,
		private router: Router,
		private crossFormationReportService: CrossFormationReportService,
		private errorService: ErrorService
	) {
		super(pageService, translate);
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant('cross-formation-report_common_name'),
			breadcrumbs: [
				{
					label: this.translate.instant(
						'cross-formation-report_common_name'
					),
				},
			],
		};
	}

	ngOnInit() {
		super.ngOnInit();

		// Search dropdowns (debounce 300ms)
		this.reportSearchChange$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this.unsubscribe)
			)
			.subscribe((v) => this.searchReports(v));
		this.themeSearchChange$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this.unsubscribe)
			)
			.subscribe((v) => this.searchThemes(v));
		this.formateurSearchChange$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this.unsubscribe)
			)
			.subscribe((v) => this.searchFormateurs(v));
		this.sourceReportSearchChange$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this.unsubscribe)
			)
			.subscribe((v) => this.searchSourceReports(v));
		this.commanditaireSearchChange$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				takeUntil(this.unsubscribe)
			)
			.subscribe((v) => this.searchCommanditaires(v));

		// Live preview pipeline (debounce 400ms + switchMap to cancel stale calls)
		this.filtersChange$
			.pipe(
				debounceTime(400),
				switchMap(() => {
					if (!this.canPreview()) {
						this.previewResult = null;
						this.totals = null;
						return of(null);
					}
					this.previewLoading = true;
					return this.crossFormationReportService
						.preview(this.buildPayload())
						.then((res) => res)
						.catch((e) => {
							this.errorService.handle(e);
							return null;
						});
				}),
				tap(() => (this.previewLoading = false)),
				takeUntil(this.unsubscribe)
			)
			.subscribe((result: PreviewResult | null) => {
				this.previewResult = result;
				if (result) {
					this.reconcileExclusions(result.formations);
					this.recomputeTotals();
				}
			});

		// Initial loads of select options
		this.searchReports('');
		this.searchSourceReports('');
		this.searchThemes('');
		this.searchFormateurs('');
		this.searchCommanditaires('');
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	// --- Search handlers ---

	private async fetchAllReports(name: string): Promise<any[]> {
		const pageSize = 100;
		const all: any[] = [];
		let page = 0;
		// Hard safety cap to avoid runaway loops
		while (page < 20) {
			// Note: on ne filtre PAS duplicated=false ici (query MongoDB stricte),
			// car certains rapports voulus ont duplicated=null. Exclusion faite
			// côté client dans filterAndMapReports (r.duplicated !== true).
			let url = `${environment.api.uri}/admin/report?_page=${page}&_limit=${pageSize}&grouped=true&_sort=shortcode&_order=asc`;
			if (name) url += `&name=${encodeURIComponent(name)}`;
			const res = await this.http
				.get<any>(url, { withCredentials: true })
				.toPromise();
			const items = (res && res.items) || [];
			all.push(...items);
			if (items.length < pageSize) break;
			page++;
		}
		return all;
	}

	// Whitelist : ce qu'on veut afficher (famille + V2 + couleur).
	private static readonly REQUIRED_FAMILIES = ['FPN', 'FDN', 'FPD', 'FDD'];
	private static readonly REQUIRED_VERSION = 'V2';
	private static readonly REQUIRED_COLORS = ['Vert', 'Bleu', 'Orange'];

	// Blacklist : cas particuliers à exclure malgré la whitelist.
	// Ajouter ici les patterns de rapports indésirables qui satisferaient
	// par accident les 3 conditions ci-dessus.
	private static readonly BLACKLIST_PATTERNS: RegExp[] = [];

	private filterAndMapReports(items: any[]): any[] {
		const Cls = MoleculeCrossFormationReportFilterComponent;
		return items
			.filter((r: any) => {
				if (r.duplicated === true) return false;
				const sc = r.shortcode || '';
				const passWhitelist =
					Cls.REQUIRED_FAMILIES.some((f) => sc.indexOf(f) >= 0) &&
					sc.indexOf(Cls.REQUIRED_VERSION) >= 0 &&
					Cls.REQUIRED_COLORS.some((c) => sc.indexOf(c) >= 0);
				const blacklisted = Cls.BLACKLIST_PATTERNS.some((re) =>
					re.test(sc)
				);
				return passWhitelist && !blacklisted;
			})
			.map((r: any) => ({ label: r.shortcode || r.name, value: r._id }));
	}

	async searchReports(name: string) {
		this.reportLoading = true;
		try {
			const items = await this.fetchAllReports(name);
			this.reportOptions = this.filterAndMapReports(items);
		} catch (e) {
			this.errorService.handle(e);
		}
		this.reportLoading = false;
	}

	async searchSourceReports(name: string) {
		this.sourceReportLoading = true;
		try {
			const items = await this.fetchAllReports(name);
			this.sourceReportOptions = this.filterAndMapReports(items);
		} catch (e) {
			this.errorService.handle(e);
		}
		this.sourceReportLoading = false;
	}

	searchThemes(label: string) {
		this.themeLoading = true;
		let url = `${environment.api.uri}/admin/formation-theme?_page=0&_limit=100&_sort=created_at&_order=desc`;
		if (label) url += `&label=${encodeURIComponent(label)}`;
		this.http.get<any>(url, { withCredentials: true }).subscribe(
			(res) => {
				const items = (res && res.items) || [];
				this.themeOptions = items.map((t: any) => ({
					label: t.label,
					value: t._id,
				}));
				this.themeLoading = false;
			},
			() => (this.themeLoading = false)
		);
	}

	searchFormateurs(name: string) {
		this.formateurLoading = true;
		let url = `${environment.api.uri}/admin/user?_page=0&_limit=50&role=coach&_sort=last_name&_order=asc`;
		if (name) url += `&last_name=${encodeURIComponent(name)}`;
		this.http.get<any>(url, { withCredentials: true }).subscribe(
			(res) => {
				const items = (res && res.items) || [];
				this.formateurOptions = items.map((u: any) => ({
					label: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
					value: u._id,
				}));
				this.formateurLoading = false;
			},
			() => (this.formateurLoading = false)
		);
	}

	searchCommanditaires(name: string) {
		this.commanditaireLoading = true;
		let url = `${environment.api.uri}/admin/user?_page=0&_limit=50&role=sponsor&_sort=last_name&_order=asc`;
		if (name) url += `&last_name=${encodeURIComponent(name)}`;
		this.http.get<any>(url, { withCredentials: true }).subscribe(
			(res) => {
				const items = (res && res.items) || [];
				this.commanditaireOptions = items.map((u: any) => ({
					label: (u.last_name || u.first_name || '').trim() || u._id,
					value: u._id,
				}));
				this.commanditaireLoading = false;
			},
			() => (this.commanditaireLoading = false)
		);
	}

	// --- Filter change handler (called by template) ---

	onFiltersChange() {
		this.filtersChange$.next();
	}

	// --- Preview helpers ---

	canPreview(): boolean {
		return (
			!!this.selectedReport &&
			!!this.selectedSourceReports &&
			this.selectedSourceReports.length > 0
		);
	}

	private buildPayload(): CrossFormationReportPayload {
		const payload: CrossFormationReportPayload = {
			report: this.selectedReport,
			source_reports: this.selectedSourceReports,
			filters: {},
		};
		if (this.selectedThemes && this.selectedThemes.length > 0) {
			payload.filters.themes = this.selectedThemes;
		}
		if (this.selectedFormateurs && this.selectedFormateurs.length > 0) {
			payload.filters.formateurs = this.selectedFormateurs;
		}
		if (
			this.selectedCommanditaires &&
			this.selectedCommanditaires.length > 0
		) {
			payload.filters.commanditaires = this.selectedCommanditaires;
		}
		if (this.withComments) {
			payload.filters.with_comments = true;
		}
		if (this.dateRange && this.dateRange.length === 2) {
			payload.filters.date_start = Helpers.convertToTimestamp(
				this.dateRange[0]
			);
			payload.filters.date_end = Helpers.convertToTimestamp(
				this.dateRange[1]
			);
		}
		return payload;
	}

	/**
	 * Drop exclusions for formations that no longer match the new filters.
	 * The user's intent (exclusion) is preserved for formations still present.
	 */
	private reconcileExclusions(formations: PreviewFormation[]) {
		const stillPresent: { [id: string]: boolean } = {};
		for (const f of formations) stillPresent[f._id] = true;
		const next: { [id: string]: boolean } = {};
		for (const id of Object.keys(this.excludedFormationIds)) {
			if (stillPresent[id]) next[id] = true;
		}
		this.excludedFormationIds = next;
	}

	// --- Selection actions ---

	isExcluded(formationId: string): boolean {
		return !!this.excludedFormationIds[formationId];
	}

	toggleFormation(formationId: string) {
		if (this.excludedFormationIds[formationId]) {
			delete this.excludedFormationIds[formationId];
		} else {
			this.excludedFormationIds[formationId] = true;
		}
		this.recomputeTotals();
	}

	selectAll() {
		this.excludedFormationIds = {};
		this.recomputeTotals();
	}

	deselectAll() {
		if (!this.previewResult) return;
		const next: { [id: string]: boolean } = {};
		for (const f of this.previewResult.formations) next[f._id] = true;
		this.excludedFormationIds = next;
		this.recomputeTotals();
	}

	get hasExclusion(): boolean {
		return Object.keys(this.excludedFormationIds).length > 0;
	}

	get includedCount(): number {
		if (!this.previewResult) return 0;
		return this.previewResult.formations.filter(
			(f) => !this.isExcluded(f._id)
		).length;
	}

	toggleExpand(formationId: string) {
		if (this.expandedFormationIds[formationId]) {
			delete this.expandedFormationIds[formationId];
		} else {
			this.expandedFormationIds[formationId] = true;
		}
	}

	isExpanded(formationId: string): boolean {
		return !!this.expandedFormationIds[formationId];
	}

	// --- Client-side totals (recomputed instantly on toggle) ---

	private recomputeTotals() {
		if (!this.previewResult) {
			this.totals = null;
			return;
		}
		const included = this.previewResult.formations.filter(
			(f) => !this.isExcluded(f._id)
		);

		let rapportsCount = 0;
		const respondantSet: { [id: string]: boolean } = {};
		const formateurSet: { [id: string]: boolean } = {};
		const surveyAcc: {
			[id: string]: { results: number; expected: number };
		} = {};

		for (const meta of this.previewResult.surveys_meta) {
			surveyAcc[meta._id] = { results: 0, expected: 0 };
		}

		for (const f of included) {
			rapportsCount += f.thematiques_count;
			for (const rid of f.respondants_ids) respondantSet[rid] = true;
			for (const cid of f.formateur_ids) formateurSet[cid] = true;
			for (const s of f.surveys) {
				if (!surveyAcc[s.survey_id]) {
					surveyAcc[s.survey_id] = { results: 0, expected: 0 };
				}
				surveyAcc[s.survey_id].results += s.results_count;
				surveyAcc[s.survey_id].expected += s.expected_count;
			}
		}

		const surveys: SurveyTotal[] = this.previewResult.surveys_meta.map(
			(meta) => {
				const acc = surveyAcc[meta._id] || { results: 0, expected: 0 };
				const percent =
					acc.expected > 0
						? Math.round((acc.results / acc.expected) * 1000) / 10
						: 0;
				return {
					survey_id: meta._id,
					label: meta.label,
					results_count: acc.results,
					expected_count: acc.expected,
					percent,
				};
			}
		);

		this.totals = {
			formations_count: included.length,
			rapports_count: rapportsCount,
			respondants_count: Object.keys(respondantSet).length,
			formateurs_count: Object.keys(formateurSet).length,
			surveys,
		};
	}

	// --- Display helpers ---

	formatDate(timestamp: number): string {
		if (!timestamp) return '-';
		return new Date(timestamp).toLocaleDateString('fr-FR');
	}

	formateurNameById(id: string): string {
		if (!this.previewResult) return '';
		const meta = this.previewResult.formateurs_meta.find(
			(m) => m._id === id
		);
		if (!meta) return '';
		return `${meta.first_name || ''} ${meta.last_name || ''}`.trim();
	}

	formateurNamesForFormation(f: PreviewFormation): string {
		return f.formateur_ids
			.map((id) => this.formateurNameById(id))
			.filter((n) => !!n)
			.join(', ');
	}

	// --- Generate ---

	async generate() {
		if (!this.canPreview() || this.includedCount === 0) return;
		this.generating = true;
		try {
			const payload = this.buildPayload();
			const excluded = Object.keys(this.excludedFormationIds);
			if (excluded.length > 0) payload.excluded_formations = excluded;
			const result = await this.crossFormationReportService.generate(
				payload
			);
			this.crossFormationReportService.currentPayload = payload;
			this.crossFormationReportService.generateResult = result;
			this.router.navigate(['/cross-formation-report', 'result']);
		} catch (e) {
			this.errorService.handle(e);
		}
		this.generating = false;
	}
}
